// REA-specific HTML parser + bot-detection signature. Lives in the adapter
// package so the generic firebase-functions / scrape-providers stack stays
// site-agnostic.
//
// Price: the page embeds REA's own listing record in window.ArgonautExchange
// (GraphQL cache, JSON nested in strings). details.listing.price is the
// SoldPrice, e.g. { display: "$35,000", __typename: "SoldPrice" } or
// "Contact agent" when withheld. It is read first; the visible
// <span class="property-info__price"> is the fallback for pages without it.
// Never the description: it carries asking prices ("Price: $370,000") and
// rents ("$1484.00 / calendar month") that were stored as sale prices.
// Verified on seven stored sold pages (2026-09-25/26), _tests_/fixtures/rea-price.
//
// Bedrooms, bathrooms, parking, property type and land size come from the same listing record
// (generalFeatures, propertyType, propertySizes.land), else from the listing header's own feature
// list. Never a page-wide first match: recent-sales cards for other properties carry
// "2 bedrooms 1 bathroom 1 car space" labels too, and stored 2 beds on 1-bed units.
//
// Sold date: the same listing record's dateSold, { display: "20 Aug 2018", __typename: "DateSold" }.
// Never a page-wide first match while that record is there: "similar properties" and recent-sales
// cards carry their own sold dates. Only a page without the listing JSON falls back to the first
// <span>Sold on 18 Feb 2023</span>.
//
// Bot-detection signature: REA serves a poisoned response when it flags the
// request as a bot — real address (from URL slug) but priceDisplay="$700"
// and every other dynamic field empty. Observed in the wild: 70% of REA
// scrapes via Zyte stealth matched this. Flag it so the scrape worker
// triggers the fallback provider chain instead of saving fake data.

import {
  type DiscoveryPreview,
  parseSalePriceText,
  rejectImplausibleSalePrice,
} from "@repo/adapter-core";
import { REALESTATE_ADAPTER_META } from "./meta.ts";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function parseJsonString(value: unknown): unknown {
  if (typeof value !== "string") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

/** Each query result in window.ArgonautExchange → <app>.urqlClientCache (JSON string) → <entry>.data (JSON string). */
function readReaQueryData(html: string): Record<string, unknown>[] {
  const script = html.match(/window\.ArgonautExchange=(\{[\s\S]*?\});?\s*<\/script>/);
  const exchange = parseJsonString(script?.[1]);
  if (!isRecord(exchange)) return [];
  const results: Record<string, unknown>[] = [];
  for (const app of Object.values(exchange)) {
    const cache = isRecord(app) ? parseJsonString(app.urqlClientCache) : null;
    if (!isRecord(cache)) continue;
    for (const entry of Object.values(cache)) {
      const data = isRecord(entry) ? parseJsonString(entry.data) : null;
      if (isRecord(data)) results.push(data);
    }
  }
  return results;
}

/** details.listing: the detail page's own listing record. */
function readReaListing(html: string): Record<string, unknown> | null {
  for (const data of readReaQueryData(html)) {
    const listing = isRecord(data.details) ? data.details.listing : null;
    if (isRecord(listing)) return listing;
  }
  return null;
}

function reaListingType(listing: Record<string, unknown> | null): "sold" | "buy" | "rent" | null {
  const typename = typeof listing?.__typename === "string" ? listing.__typename : "";
  if (typename.startsWith("Sold")) return "sold";
  if (typename.startsWith("Buy")) return "buy";
  if (typename.startsWith("Rent")) return "rent";
  return null;
}

function trimmed(value: unknown): string | null {
  return typeof value === "string" && value.trim() ? value.trim() : null;
}

/** generalFeatures.<name> is an IntValue: { value: 3, __typename: "IntValue" }. */
function intValue(value: unknown): number | null {
  const n = isRecord(value) ? value.value : null;
  return typeof n === "number" && Number.isInteger(n) && n >= 0 ? n : null;
}

const SQM_PER_UNIT: Readonly<Record<string, number>> = { "m²": 1, m2: 1, sqm: 1, ha: 10_000, acres: 4046.86 };

/** propertySizes.land: { displayValue: "650", sizeUnit: { displayValue: "m²" } }, converted to sqm. */
function landSqm(land: unknown): string | null {
  if (!isRecord(land) || !isRecord(land.sizeUnit)) return null;
  const size = Number(trimmed(land.displayValue)?.replace(/,/g, ""));
  const perUnit = SQM_PER_UNIT[trimmed(land.sizeUnit.displayValue)?.toLowerCase() ?? ""];
  return perUnit && size > 0 ? String(Math.round(size * perUnit)) : null;
}

const MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];

/** "20 Aug 2018" (dateSold.display, or the page's "Sold on" line) → "2018-08-20"; else null. */
function soldDate(display: string | null): string | null {
  const match = display?.match(/^(\d{1,2})\s+([A-Za-z]{3,9})\s+(\d{4})$/);
  if (!match) return null;
  const month = MONTHS.indexOf(match[2].slice(0, 3).toLowerCase());
  const day = Number(match[1]);
  const year = Number(match[3]);
  const date = new Date(Date.UTC(year, month, day));
  if (month < 0 || date.getUTCFullYear() !== year || date.getUTCMonth() !== month || date.getUTCDate() !== day) {
    return null;
  }
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

type Features = Record<
  "bedrooms" | "bathrooms" | "parking" | "propertyType" | "landSize" | "landSizeUnit",
  string | number | null
>;

// Explicit nulls: the merge (merge-parser-data.ts) clears the generic parse's guesses with them.
const NO_FEATURES: Features = {
  bedrooms: null, bathrooms: null, parking: null, propertyType: null, landSize: null, landSizeUnit: null,
};

/** details.listing.generalFeatures / propertyType / propertySizes: the listing's own record. */
function readListingFeatures(listing: Record<string, unknown> | null): Features | null {
  const general = listing?.generalFeatures;
  if (!listing || !isRecord(general)) return null;
  const landSize = isRecord(listing.propertySizes) ? landSqm(listing.propertySizes.land) : null;
  return {
    bedrooms: intValue(general.bedrooms),
    bathrooms: intValue(general.bathrooms),
    parking: intValue(general.parkingSpaces),
    propertyType: trimmed(isRecord(listing.propertyType) ? listing.propertyType.display : null),
    landSize,
    landSizeUnit: landSize ? "sqm" : null,
  };
}

/**
 * Fallback: the listing header's own feature list, never a page-wide first match. Recent-sales
 * cards for other properties carry the same "2 bedrooms 1 bathroom 1 car space" labels.
 *   <ul class="... property-info__primary-features" aria-label="House  with 3 bedrooms ...">
 *     <li aria-label="3 bedrooms">..</li> ... <p ..>House</p></ul>    (the type closes the list)
 * A count the header omits stays null: REA drops the car-space item for 0 and for unknown alike.
 */
function readHeaderFeatures(html: string): Features | null {
  const header = html.match(/<ul\b[^>]*\bproperty-info__primary-features\b[^>]*>([\s\S]*?)<\/ul>/i)?.[1];
  if (!header) return null;
  const count = (label: string) => {
    const n = header.match(new RegExp(`<li[^>]*aria-label=["'](\\d+)\\s+${label}s?["']`, "i"))?.[1];
    return n === undefined ? null : Number(n);
  };
  return {
    ...NO_FEATURES,
    bedrooms: count("bedroom"),
    bathrooms: count("bathroom"),
    parking: count("car space"),
    propertyType: trimmed(header.match(/<p[^>]*>([^<\d][^<]*)<\/p>\s*$/)?.[1]),
  };
}

export function parseReaDetailHtml(html: string, _url: string): Record<string, unknown> {
  const hit: Record<string, unknown> = {};

  const listing = readReaListing(html);
  // The listing record's GraphQL type says what the page is: SoldResidentialListing on a sold page,
  // BuyResidentialListing on a for-sale one (stored pages, 2026-10-15). Without it the generic parse
  // called any page with a "Sold" link in its navigation sold, and a buy page's asking price a sale.
  const listingType = reaListingType(listing);
  if (listingType) hit.listingType = listingType;
  const listingPrice = trimmed(isRecord(listing?.price) ? listing.price.display : null);
  const elementPrice = trimmed(
    html.match(/class=["'][^"']*property-info__price[^"']*["'][^>]*>\s*([^<]+)</i)?.[1],
  );
  const display = listingPrice ?? elementPrice;
  if (display && display !== "$700") {
    hit.priceDisplay = display;
    hit.priceSource = listingPrice ? "listing-json" : "display";
    // Explicit null for a withheld price, a range or anything else that is not one amount: it
    // clears a guessed figure in the merge (merge-parser-data.ts) instead of letting it stand.
    hit.salePrice = parseSalePriceText(display);
    rejectImplausibleSalePrice(hit);
  }

  if (listing) {
    // Explicit null when the record holds no sold date: it clears a date the generic parse took
    // from elsewhere on the page (merge-parser-data.ts).
    hit.saleDate = soldDate(trimmed(isRecord(listing.dateSold) ? listing.dateSold.display : null));
    hit.saleDateSource = hit.saleDate ? "listing-json" : null;
  } else {
    const pageDate = soldDate(html.match(/Sold on (\d{1,2}\s+[A-Za-z]{3,9}\s+\d{4})/i)?.[1] ?? null);
    if (pageDate) {
      hit.saleDate = pageDate;
      hit.saleDateSource = "page";
    }
  }

  const listingFeatures = readListingFeatures(listing);
  const headerFeatures = listingFeatures ? null : readHeaderFeatures(html);
  Object.assign(hit, listingFeatures ?? headerFeatures ?? NO_FEATURES);
  hit.featuresSource = listingFeatures ? "listing-json" : headerFeatures ? "header" : null;

  return hit;
}

export function detectReaBotResponse(data: Record<string, unknown>): boolean {
  const priceDisplay = typeof data.priceDisplay === "string" ? data.priceDisplay.trim() : null;
  if (priceDisplay !== "$700") return false;
  const isEmpty = (k: string) => data[k] === null || data[k] === undefined || data[k] === "";
  return (
    isEmpty("salePrice")
    && isEmpty("saleDate")
    && isEmpty("bedrooms")
    && isEmpty("bathrooms")
    && isEmpty("agencyName")
  );
}

/**
 * Search-result cards (buySearch or soldSearch.results.exact), for the discovery-stage date guard
 * and follow-until-sold option B. Only `exact`: REA pads a suburb's results with `surrounding`
 * listings from neighbouring suburbs (Hampton buy, 2026-10-25: page 15 of 35 had 0 exact, 25
 * surrounding), so the first page with no exact card is the end of the suburb. Cards that are not
 * listing pages (ProjectProfile, /project/...) are skipped. Null when the page has no search cache.
 */
export function parseReaDiscoveryPreviews(html: string, _pageUrl: string): DiscoveryPreview[] | null {
  for (const data of readReaQueryData(html)) {
    const search = isRecord(data.buySearch) ? data.buySearch : isRecord(data.soldSearch) ? data.soldSearch : null;
    const results = search && isRecord(search.results) ? search.results : null;
    if (!results) continue;
    const exact = isRecord(results.exact) && Array.isArray(results.exact.items) ? results.exact.items : [];
    const cards: DiscoveryPreview[] = [];
    for (const item of exact) {
      const listing = isRecord(item) && isRecord(item.listing) ? item.listing : null;
      const links = listing && isRecord(listing._links) ? listing._links : null;
      const url = links && isRecord(links.canonical) ? trimmed(links.canonical.href) : null;
      if (!listing || !url || !REALESTATE_ADAPTER_META.urlPattern.test(url)) continue;
      const card: DiscoveryPreview = { url };
      const address = isRecord(listing.address) ? listing.address : null;
      const display = address && isRecord(address.display) ? address.display : null;
      const street = display ? trimmed(display.shortAddress) : null;
      const suburb = address ? trimmed(address.suburb) : null;
      const state = address ? trimmed(address.state)?.toUpperCase() : null;
      const postcode = address ? trimmed(address.postcode) : null;
      const features = isRecord(listing.generalFeatures) ? listing.generalFeatures : null;
      const sizes = isRecord(listing.propertySizes) ? listing.propertySizes : null;
      const auction = isRecord(listing.auction) && isRecord(listing.auction.dateTime) ? listing.auction.dateTime : null;
      const fields: Partial<DiscoveryPreview> = {
        listingId: trimmed(listing.id) ?? undefined,
        street: street ?? undefined,
        suburb: suburb ?? undefined,
        state: state ?? undefined,
        postcode: postcode ?? undefined,
        address: street ? [street, [suburb, state, postcode].filter(Boolean).join(" ")].filter(Boolean).join(", ") : undefined,
        priceDisplay: isRecord(listing.price) ? (trimmed(listing.price.display) ?? undefined) : undefined,
        saleDate: isRecord(listing.dateSold) ? (soldDate(trimmed(listing.dateSold.display)) ?? undefined) : undefined,
        propertyType: isRecord(listing.propertyType) ? (trimmed(listing.propertyType.display) ?? undefined) : undefined,
        bedrooms: intValue(features?.bedrooms) ?? undefined,
        bathrooms: intValue(features?.bathrooms) ?? undefined,
        parking: intValue(features?.parkingSpaces) ?? undefined,
        landSize: sizes ? (Number(landSqm(sizes.land)) || undefined) : undefined,
        auctionAt: auction ? (trimmed(auction.value) ?? undefined) : undefined,
      };
      for (const [k, v] of Object.entries(fields)) {
        if (v !== undefined) (card as unknown as Record<string, unknown>)[k] = v;
      }
      cards.push(card);
    }
    return cards;
  }
  return null;
}
